import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
    getUsuarios, 
    getPermisos, 
    getModulos, 
    processUsuariosForTable,
    updateUserPermission 
} from '@shared/helpers/api/mantenedores';
import { userHasPermission } from '@shared/helpers/api/mantenedores/usuarios';
import { loadPermissions } from '@actions/permissionsActions';

/**
 * Hook para manejar la gestión de usuarios y permisos
 */
export const useUsuariosManagement = () => {
    const dispatch = useDispatch();
    const currentUser = useSelector(state => state.auth.user);
    const permissionsState = useSelector(state => state.permissions);
    const userPermissions = permissionsState?.permissions;

    // Si no hay permisos, despachar la acción para cargarlos
    useEffect(() => {
        if (!userPermissions && currentUser) {
            dispatch(loadPermissions(currentUser));
        }
    }, [userPermissions, currentUser, dispatch]);

    // Estados principales
    const [usuarios, setUsuarios] = useState([]);
    const [permisos, setPermisos] = useState([]);
    const [modulos, setModulos] = useState([]);
    
    // Estados de carga
    const [loading, setLoading] = useState(true);
    const [loadingUpdate, setLoadingUpdate] = useState(false);
    const [error, setError] = useState(null);

    // Cargar datos iniciales
    const loadData = useCallback(async () => {
        if (!currentUser) return;

        setLoading(true);
        setError(null);

        try {
            // Cargar datos en paralelo
            const [usuariosRes, permisosRes, modulosRes] = await Promise.all([
                getUsuarios(currentUser),
                getPermisos(currentUser),
                getModulos(currentUser)
            ]);

            // Si hay errores, usar datos mock para desarrollo
            let permisosData, modulosData, usuariosData;

            if (!usuariosRes.success || !permisosRes.success || !modulosRes.success) {
                console.warn('⚠️ Error en APIs, usando datos mock para desarrollo');
                
                // Datos mock para desarrollo
                permisosData = [
                    { id: 1, nombre: 'vista', descripcion: 'Ver información' },
                    { id: 2, nombre: 'crear', descripcion: 'Crear nuevos registros' },
                    { id: 3, nombre: 'editar', descripcion: 'Editar registros existentes' },
                    { id: 4, nombre: 'eliminar', descripcion: 'Eliminar registros' },
                    { id: 5, nombre: 'exportar', descripcion: 'Exportar datos' },
                    { id: 6, nombre: 'administrar', descripcion: 'Administrar completamente' }
                ];

                modulosData = [
                    { id: 23, modulo: 'conductores', descripcion: 'Gestión de conductores del sistema' },
                    { id: 24, modulo: 'empresas', descripcion: 'Gestión de empresas transportistas' },
                    { id: 25, modulo: 'rutas', descripcion: 'Gestión de rutas de transporte' },
                    { id: 26, modulo: 'extras', descripcion: 'Gestión de servicios extras' },
                    { id: 27, modulo: 'tarifarios', descripcion: 'Gestión de tarifarios y precios' },
                    { id: 28, modulo: 'mantenedores_generales', descripcion: 'Gestión de mantenedores generales' },
                    { id: 29, modulo: 'usuarios', descripcion: 'Gestión de usuarios y permisos del sistema' }
                ];

                usuariosData = [
                    {
                        id: 1,
                        nombre: 'Eduardo Aguirrebena',
                        email: 'eduardo@spread.cl',
                        activo: true,
                        usuario: 'ewZe2rwnTHYy0RfNqqmJv8SJlD42',
                        permisos: [
                            {
                                modulo: 'conductores',
                                modulo_id: 23,
                                descripcion: 'Gestión de conductores del sistema',
                                permisos: [
                                    { id: 3, permiso: 'vista', descripcion: 'Permiso para ver y consultar datos del módulo' },
                                    { id: 4, permiso: 'crear', descripcion: 'Permiso para crear nuevos registros en el módulo' }
                                ]
                            },
                            {
                                modulo: 'empresas',
                                modulo_id: 24,
                                descripcion: 'Gestión de empresas transportistas',
                                permisos: [
                                    { id: 3, permiso: 'vista', descripcion: 'Permiso para ver y consultar datos del módulo' },
                                    { id: 5, permiso: 'editar', descripcion: 'Permiso para modificar registros existentes del módulo' }
                                ]
                            },
                            {
                                modulo: 'usuarios',
                                modulo_id: 29,
                                descripcion: 'Gestión de usuarios y permisos del sistema',
                                permisos: [
                                    { id: 3, permiso: 'vista', descripcion: 'Permiso para ver y consultar datos del módulo' },
                                    { id: 8, permiso: 'administrar', descripcion: 'Permiso completo de administración del módulo' },
                                    { id: 5, permiso: 'editar', descripcion: 'Permiso para modificar registros existentes del módulo' }
                                ]
                            }
                        ]
                    }
                ];
            } else {
                // Verificar errores individuales
                if (!usuariosRes.success) {
                    throw new Error(usuariosRes.error);
                }
                if (!permisosRes.success) {
                    throw new Error(permisosRes.error);
                }
                if (!modulosRes.success) {
                    throw new Error(modulosRes.error);
                }

                // Procesar datos reales de la API
                permisosData = permisosRes.data.data;
                modulosData = modulosRes.data.data;
                usuariosData = usuariosRes.data.data;
            }

            setPermisos(permisosData);
            setModulos(modulosData);

            // Procesar usuarios para la tabla
            const usuariosProcesados = processUsuariosForTable(usuariosData, modulosData, permisosData);
            setUsuarios(usuariosProcesados);

            // console.log('✅ Datos cargados:', {
            //     usuarios: usuariosProcesados.length,
            //     permisos: permisosData.length,
            //     modulos: modulosData.length
            // });

        } catch (err) {
            console.error('❌ Error cargando datos:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    // Actualizar permiso de usuario
    const updatePermission = useCallback(async (userId, modulo, permiso, action) => {
        if (!currentUser) return { success: false, error: 'Usuario no autenticado' };

        setLoadingUpdate(true);

        try {
            const result = await updateUserPermission(currentUser, userId, modulo, permiso, action);
            
            if (result.success) {
                // Recargar datos para mostrar cambios
                await loadData();
                console.log(`✅ Permiso ${action === 'add' ? 'agregado' : 'removido'}:`, { userId, modulo, permiso });
            }

            return result;
        } catch (err) {
            console.error('❌ Error actualizando permiso:', err);
            return { success: false, error: err.message };
        } finally {
            setLoadingUpdate(false);
        }
    }, [currentUser, loadData]);

    // Procesar el usuario actual para asegurar que tenga la propiedad 'modulos'
    const getProcessedCurrentUser = useCallback(() => {
        if (!userPermissions) return null;
        // Simular estructura esperada por userHasPermission
        if (userPermissions.modules) {
            const modulos = {};
            Object.entries(userPermissions.modules).forEach(([modKey, modData]) => {
                modulos[modKey] = {
                    permisos: (modData.permissions || []).map(p => ({ permiso: p }))
                };
            });
            return { ...userPermissions, modulos };
        }
        return null;
    }, [userPermissions]);

    // Verificar si el usuario actual puede editar usuarios
    const canEditUsers = useCallback(() => {
        const user = getProcessedCurrentUser();
        if (!user) return false;
        return userHasPermission(user, 'usuarios', 'editar') || userHasPermission(user, 'usuarios', 'administrar');
    }, [getProcessedCurrentUser]);

    // Verificar si el usuario actual puede administrar usuarios
    const canAdminUsers = useCallback(() => {
        const user = getProcessedCurrentUser();
        if (!user) return false;
        return userHasPermission(user, 'usuarios', 'administrar');
    }, [getProcessedCurrentUser]);

    // Cargar datos al montar el componente
    useEffect(() => {
        loadData();
    }, [loadData]);
    
    return {
        // Datos
        usuarios,
        permisos,
        modulos,
        userPermissions,
        
        // Estados
        loading,
        loadingUpdate,
        error,
        
        // Funciones
        loadData,
        updatePermission,
        canEditUsers,
        canAdminUsers,
        
        // Información del usuario actual
        currentUser
    };
};
